import { ConnectProto } from "@racoon/api";

const { DriverIncidentType, JobStatus } = ConnectProto;

/** The dispatcher's reasons, in the dropdown's order: the job's own, then the shift's. */
const INCIDENT_OPTIONS = [
  DriverIncidentType.PICKUP_LOCATION_BLOCKED,
  DriverIncidentType.PICKUP_LOCATION_NO_ACCESS,
  DriverIncidentType.UNSUITABLE_EQUIPMENT,
  DriverIncidentType.CONTAINERS_DAMAGED,
  DriverIncidentType.TRUCK_BREAKDOWN,
  DriverIncidentType.TRAFFIC_ACCIDENT,
  DriverIncidentType.DRIVER_UNWELL,
  DriverIncidentType.LOADERS_UNWELL,
  DriverIncidentType.OTHER,
] as const;

/** «Не успели (долг)» leads, for a Невывоз only. */
export const incidentOptions = (notCompleted: boolean): ConnectProto.DriverIncidentType[] =>
  notCompleted ? [DriverIncidentType.EXPIRED, ...INCIDENT_OPTIONS] : [...INCIDENT_OPTIONS];

/** The explanation column's size. */
export const EXPLANATION_MAX_LENGTH = 255;

/** A reason picked but not saved yet; a verdict press saves it. */
export type StagedIncident = { type: ConnectProto.DriverIncidentType; explanation: string };

/** The reason the job reads now: its newest incident's. */
export type CurrentIncident = { type: ConnectProto.DriverIncidentType; explanation?: string };

/** Only a Невывоз or a cancelled job takes a reason. */
export const takesIncident = (outcome?: ConnectProto.JobStatus): boolean =>
  outcome === JobStatus.NOT_COMPLETED || outcome === JobStatus.CANCELLED;

/** The pick that still fits `outcome`: «Не успели (долг)» only for a Невывоз. */
export const incidentPickFor = (
  picked: StagedIncident | undefined,
  outcome?: ConnectProto.JobStatus,
): StagedIncident | undefined => {
  if (!picked || !takesIncident(outcome)) return undefined;
  if (picked.type === DriverIncidentType.EXPIRED && outcome !== JobStatus.NOT_COMPLETED) {
    return undefined;
  }
  return picked;
};

/** The pick a save records; a job turned Невывоз with no reason yet gets «Не успели (долг)». */
export const pickToRecord = (
  staged: StagedIncident | undefined,
  { becomesNotCompleted, hasReason }: { becomesNotCompleted: boolean; hasReason: boolean },
): StagedIncident | undefined =>
  staged ??
  (becomesNotCompleted && !hasReason
    ? { type: DriverIncidentType.EXPIRED, explanation: "" }
    : undefined);

/** «Другое» needs words; no explanation may outgrow its column. */
export const isIncidentPickValid = ({ type, explanation }: StagedIncident): boolean => {
  const text = explanation.trim();
  if (text.length > EXPLANATION_MAX_LENGTH) return false;
  return type !== DriverIncidentType.OTHER || text.length > 0;
};

/** What a verdict press records: a valid pick that says something new. */
export const incidentToSave = (
  staged: StagedIncident | undefined,
  current: CurrentIncident | undefined,
): CurrentIncident | undefined => {
  if (!staged || !isIncidentPickValid(staged)) return undefined;
  const explanation = staged.explanation.trim() || undefined;
  if (current?.type === staged.type && (current.explanation || undefined) === explanation) {
    return undefined;
  }
  return { type: staged.type, explanation };
};
